/**
 * Notification bell + slide-in panel — shared.
 *
 * Lifted from isg-intranet's `components/notification-sidebar.js`, which was the
 * only real implementation in the estate. The CSS was ALREADY shared
 * (`.global-toolbar`, `.notification-sidebar`, `.notif-item`, … all live in
 * ui-kit/css/layout.css) — only the behaviour was trapped in one app. This is
 * the JS catching up with the design system.
 *
 * ## Why this takes an adapter rather than just a base path
 *
 * The two apps' payloads genuinely disagree, and a copy-paste would have failed
 * silently rather than loudly — blank icons, every row rendered unread, and no
 * navigation, with no error anywhere:
 *
 *   field        intranet          boardroom
 *   ----------   ---------------   -----------------
 *   type         `type`            `kind`
 *   read flag    `is_read`         `read`
 *   link         `url`             `action_path`
 *   count        `unread_count`    `unread`
 *   page param   `per_page`        `limit`
 *
 * So `normalise` and `unreadOf` are options, defaulted to the intranet's shape
 * (its current behaviour is unchanged if it ever adopts this). An app whose API
 * differs says so once, here, instead of forking the component.
 *
 * ## Notes carried over from the original, deliberately
 *
 * - **Stale indicator.** The bell polls every 60s; if a poll hasn't SUCCEEDED in
 *   60s an amber dot appears. That distinction matters: "no notifications" and
 *   "we can't reach the server" look identical on a bell otherwise, and the
 *   second one is the dangerous one.
 * - `destroy()` clears both intervals. Without it an SPA that re-mounts the
 *   shell leaks a poller per mount, and they all keep firing.
 *
 * Not migrated: isg-intranet still runs its own copy. It works and it is on the
 * critical path for every staff member — moving it deserves its own change, not
 * a drive-by. `isg-intranet/components/notification-bell.js` is meanwhile DEAD
 * (248 lines, imported by nothing) and can be deleted whenever that happens.
 */
import { api } from '../services/api.js';
import { t } from '../i18n/index.js';
import { timeAgo } from '../utils/formatters.js';

const BELL_SVG = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>';

const STALE_THRESHOLD_MS = 60_000;

const DEFAULT_ICONS = {
  mention: '💬', reply: '↩️', group_invite: '👥', group_request_approved: '✅',
  event_reminder: '📅', announcement: '📢', poll_closing: '📊',
  ticket_update: '🎫', system: '⚙️',
};

/** The intranet's shape — the default, so adopting this changes nothing there. */
const DEFAULT_NORMALISE = (n) => ({
  id: n.id, type: n.type, title: n.title, body: n.body,
  url: n.url, read: !!n.is_read, created_at: n.created_at,
});
const DEFAULT_UNREAD_OF = (res) => res.unread_count || 0;

function escHtml(str) {
  const d = document.createElement('div');
  d.textContent = str == null ? '' : String(str);
  return d.innerHTML;
}
const truncate = (s, max) => (!s ? '' : (s.length <= max ? s : `${s.substring(0, max)}...`));

// Relative time is the shared formatter (`utils/formatters.js` timeAgo): same
// thresholds as the copy that lived here, but it follows the reader's locale —
// the old one ended in a bare toLocaleDateString(), i.e. the BROWSER's.

// The group is returned as a KEY, not a label: rows are bucketed by it and
// the label is looked up when the heading is drawn.
function timeGroup(iso) {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days === 0) return 'group_today';
  if (days === 1) return 'group_yesterday';
  if (days < 7) return 'group_week';
  return 'group_older';
}

// Literal keys (not `notifications.${group}`) so a grep for a key finds its reader.
function groupLabel(group) {
  switch (group) {
    case 'group_today': return t('notifications.group_today');
    case 'group_yesterday': return t('notifications.group_yesterday');
    case 'group_week': return t('notifications.group_week');
    default: return t('notifications.group_older');
  }
}

function ensureStaleStyles() {
  if (document.getElementById('isg-notif-stale-styles')) return;
  const style = document.createElement('style');
  style.id = 'isg-notif-stale-styles';
  style.textContent = `
    .toolbar-notification-btn { position: relative; }
    .toolbar-notification-btn.stale::after {
      content: ''; position: absolute; top: 4px; right: 4px;
      width: 8px; height: 8px; border-radius: 50%;
      background: #f59e0b; border: 1.5px solid var(--surface-primary, #fff);
      pointer-events: none;
    }`;
  document.head.appendChild(style);
}

/**
 * Mount the bell + panel.
 *
 * @param {object}   opts
 * @param {string}   opts.basePath   API prefix, e.g. '/notifications' or '/boardroom/notifications'
 * @param {function} opts.onNavigate (url) => void — the app's router. Omitted = links inert.
 * @param {object}   [opts.icons]    type -> emoji
 * @param {function} [opts.normalise] raw row -> {id,type,title,body,url,read,created_at}
 * @param {function} [opts.unreadOf]  response -> unread count
 * @param {string}   [opts.viewAllUrl] footer link; the footer is hidden without it
 * @param {string}   [opts.pageParam] page-size param name ('per_page' | 'limit')
 * @param {number}   [opts.pageSize]  rows to fetch
 * @returns {{bell: HTMLElement, panel: HTMLElement, destroy: function, refresh: function}}
 */
export function createNotificationPanel(opts = {}) {
  const {
    basePath = '/notifications',
    onNavigate = null,
    icons = DEFAULT_ICONS,
    normalise = DEFAULT_NORMALISE,
    unreadOf = DEFAULT_UNREAD_OF,
    viewAllUrl = null,
    pageParam = 'per_page',   // boardroom's list endpoint reads `limit` instead
    pageSize = 30,
  } = opts;

  let isOpen = false;
  let pollTimer = null;
  let staleTimer = null;
  let lastGoodPoll = Date.now();

  ensureStaleStyles();

  // ── bell ────────────────────────────────────────────────────────────────
  const bell = document.createElement('button');
  bell.className = 'toolbar-notification-btn';
  bell.setAttribute('aria-label', t('notifications.title'));
  bell.innerHTML = `${BELL_SVG}<span class="notification-badge" style="display:none;"></span>`;

  // ── panel ───────────────────────────────────────────────────────────────
  const panel = document.createElement('aside');
  panel.className = 'notification-sidebar';
  panel.id = 'notification-sidebar';
  panel.setAttribute('aria-label', t('notifications.title'));
  panel.innerHTML = `
    <div class="notification-sidebar-header">
      <h2>${escHtml(t('notifications.title'))}</h2>
      <div class="notification-sidebar-actions">
        <button class="btn-ghost-sm" data-notif="mark-all">${escHtml(t('notifications.mark_all_read'))}</button>
        <button class="notification-sidebar-close" data-notif="close" aria-label="${escHtml(t('notifications.close'))}">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>
    </div>
    <div class="notification-sidebar-tabs">
      <button class="notification-tab active" data-filter="all">${escHtml(t('notifications.all'))}</button>
      <button class="notification-tab" data-filter="unread">${escHtml(t('notifications.unread'))}</button>
    </div>
    <div class="notification-sidebar-list" data-notif="list">
      <div style="padding:var(--space-6);text-align:center;"><div class="spinner" style="margin:0 auto;"></div></div>
    </div>
    ${viewAllUrl ? `<div class="notification-sidebar-footer">
      <a href="${escHtml(viewAllUrl)}" data-link class="notification-view-all" data-notif="view-all">${escHtml(t('notifications.view_all'))}</a>
    </div>` : ''}`;

  const listEl = panel.querySelector('[data-notif="list"]');

  function setBadge(count) {
    const badge = bell.querySelector('.notification-badge');
    if (!badge) return;
    badge.style.display = count > 0 ? '' : 'none';
    badge.textContent = count > 99 ? '99+' : String(count);
  }

  function updateStale() {
    const stale = (Date.now() - lastGoodPoll) > STALE_THRESHOLD_MS;
    bell.classList.toggle('stale', stale);
    if (stale) bell.setAttribute('title', t('notifications.stale'));
    else bell.removeAttribute('title');
  }

  async function load(filter = 'all') {
    listEl.innerHTML = '<div style="padding:var(--space-6);text-align:center;"><div class="spinner" style="margin:0 auto;"></div></div>';
    try {
      const q = `?${pageParam}=${pageSize}${filter === 'unread' ? '&unread=true' : ''}`;
      const res = await api.get(`${basePath}${q}`);
      const rows = (res.notifications || []).map(normalise);
      setBadge(unreadOf(res));
      if (!rows.length) {
        listEl.innerHTML = `<div class="notification-empty">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--text-quaternary)" stroke-width="1.5">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
          </svg><p>${escHtml(t('notifications.caught_up'))}</p></div>`;
        return;
      }
      const groups = {};
      for (const n of rows) (groups[timeGroup(n.created_at)] ||= []).push(n);
      let html = '';
      for (const [name, items] of Object.entries(groups)) {
        html += `<div class="notif-group-label">${escHtml(groupLabel(name))}</div>`;
        for (const n of items) {
          html += `<div class="notif-item${n.read ? '' : ' unread'}" data-id="${n.id}" data-url="${escHtml(n.url || '')}">
            <div class="notif-icon">${icons[n.type] || '🔔'}</div>
            <div class="notif-content">
              <div class="notif-title">${escHtml(n.title)}</div>
              ${n.body ? `<div class="notif-body">${escHtml(truncate(n.body, 100))}</div>` : ''}
              <div class="notif-time">${timeAgo(n.created_at)}</div>
            </div>
            ${n.read ? '' : '<div class="notif-unread-dot"></div>'}
          </div>`;
        }
      }
      listEl.innerHTML = html;
    } catch {
      listEl.innerHTML = `<div style="padding:var(--space-6);text-align:center;color:var(--text-tertiary);font-size:var(--text-sm);">${escHtml(t('notifications.load_failed'))}</div>`;
    }
  }

  async function poll() {
    try {
      const res = await api.get(`${basePath}/unread-count`);
      setBadge(unreadOf(res));
      lastGoodPoll = Date.now();
    } catch {
      // Deliberately do NOT touch lastGoodPoll — the bell going stale is the
      // signal. "Nothing new" and "can't reach the server" must not look alike.
    }
    updateStale();
  }

  const open = () => { isOpen = true; panel.classList.add('open'); load('all'); };
  const close = () => { isOpen = false; panel.classList.remove('open'); };

  bell.addEventListener('click', (e) => { e.stopPropagation(); if (isOpen) close(); else open(); });
  panel.querySelector('[data-notif="close"]').addEventListener('click', close);
  panel.querySelector('[data-notif="mark-all"]').addEventListener('click', async () => {
    try {
      await api.post(`${basePath}/read-all`, {});
      setBadge(0);
      panel.querySelectorAll('.notif-item.unread').forEach((el) => el.classList.remove('unread'));
      panel.querySelectorAll('.notif-unread-dot').forEach((el) => el.remove());
    } catch { /* silent — the badge will self-correct on the next poll */ }
  });
  panel.querySelectorAll('.notification-tab').forEach((tab) => {
    tab.addEventListener('click', () => {
      panel.querySelectorAll('.notification-tab').forEach((x) => x.classList.remove('active'));
      tab.classList.add('active');
      load(tab.dataset.filter);
    });
  });
  listEl.addEventListener('click', async (e) => {
    const item = e.target.closest('.notif-item');
    if (!item) return;
    if (item.classList.contains('unread')) {
      try {
        const res = await api.post(`${basePath}/${item.dataset.id}/read`, {});
        item.classList.remove('unread');
        item.querySelector('.notif-unread-dot')?.remove();
        // Trust the server's count rather than decrementing locally — two tabs
        // open would otherwise drift apart and never reconcile.
        setBadge(unreadOf(res));
      } catch { /* silent */ }
    }
    if (item.dataset.url && onNavigate) { close(); onNavigate(item.dataset.url); }
  });
  panel.querySelector('[data-notif="view-all"]')?.addEventListener('click', (e) => {
    e.preventDefault(); close();
    if (onNavigate) onNavigate(viewAllUrl);
  });

  const onKey = (e) => { if (e.key === 'Escape' && isOpen) close(); };
  document.addEventListener('keydown', onKey);

  poll();
  pollTimer = setInterval(poll, 60_000);
  staleTimer = setInterval(updateStale, 15_000);

  return {
    bell,
    panel,
    refresh: () => poll(),
    destroy() {
      clearInterval(pollTimer); clearInterval(staleTimer);
      document.removeEventListener('keydown', onKey);
      pollTimer = staleTimer = null; isOpen = false;
    },
  };
}
